import { useEffect, useState } from 'react';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { Box, IconButton, MenuItem, Select, SelectChangeEvent, TextField, Tooltip } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useDebouncedValue } from 'hooks';
import { displayErrorMessage } from 'utilities/displayErrorMessage';
import { JobsFiltersProps } from './filters.props';

export const JobFilters = ({ 
  statusOptions, 
  statusOptionsLoading, 
  onFiltersChange
}: JobsFiltersProps) => {
  const [searchParam, setSearchParam] = useState('');
  const [searchStatus, setSearchStatus] = useState('default');
  const [fromValuationDate, setFromValuationDate] = useState<Date | null>(null);
  const [toValuationDate, setToValuationDate] = useState<Date | null>(null);

  const debouncedSearchParam = useDebouncedValue(searchParam, 500);

  useEffect(() => {
    onFiltersChange({
      searchParam: debouncedSearchParam,
      searchStatus,
      fromDate: fromValuationDate,
      toDate: toValuationDate,
    });
  }, [debouncedSearchParam, searchStatus, fromValuationDate, toValuationDate, onFiltersChange]);

  const handleSearchParamChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchParam(event.target.value);
  };

  const handleSearchStatusChange = (event: SelectChangeEvent<string>) => {
    setSearchStatus(event.target.value);
  };

  const handleFromValuationDateChange = (date: Date | null) => {
    setFromValuationDate(date);
  };

  const handleToValuationDateChange = (date: Date | null) => {
    if (fromValuationDate && date && fromValuationDate > date) {
      displayErrorMessage('From Valuation Date must be before To Valuation Date');
      return;
    }
    setToValuationDate(date);
  };

  const cleanFilters = () => {
    setSearchParam('');
    setSearchStatus('default');
    setFromValuationDate(null);
    setToValuationDate(null);
  };

  return (
    <Box display="flex" alignItems="center" gap={1} sx={{ marginLeft: 2 }}>
      <TextField label="Search" variant="outlined" value={searchParam} onChange={handleSearchParamChange} />
      <Select label="Status" variant="outlined" value={searchStatus} onChange={handleSearchStatusChange} sx={{ width: 200 }}>
        <MenuItem disabled value="default" selected> Select Status </MenuItem>
        {statusOptionsLoading ? (
          <MenuItem disabled> Loading... </MenuItem>
        ) : (
          statusOptions.map(option => (
            <MenuItem key={option.id.option_id} value={option.title}>
              {option.title}
            </MenuItem>
          ))
        )}
      </Select>
      <DatePicker
        label="From Valuation Date"
        value={fromValuationDate}
        onChange={handleFromValuationDateChange}
        slotProps={{
          textField: {
            variant: 'outlined',
          },
        }}
      />
      <DatePicker
        label="To Valuation Date"
        value={toValuationDate}
        onChange={handleToValuationDateChange}
        minDate={fromValuationDate || undefined}
        slotProps={{
          textField: {
            variant: 'outlined',
          },
        }}
      />
      <Tooltip title="Clear filters">
        <IconButton onClick={cleanFilters}>
          <FilterAltOffIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
};